import {useEffect,useRef,useState} from 'react';
import supabase from '../supabase/supabase';
import {interviewDeletionBlocker,loadWorkspaceRow} from '../lib/projectWorkspace';
import {reviewRpc} from '../lib/clientReview';
import {createProjectStore} from '../lib/cloudProjects';
import {cachedPrivateMediaUrl} from '../lib/privateMediaUrlCache';
const store=createProjectStore(supabase);
export default function useCloudProjects({session,project,onOpen,onRename,onClear,onError}) {
 const [rows,setRows]=useState([]),[trashRows,setTrashRows]=useState([]),[mediaLibrary,setMediaLibrary]=useState([]),[active,setActive]=useState(null),[busy,setBusy]=useState(false),[status,setStatus]=useState('');
 const [renaming,setRenaming]=useState(false);
 const activeRef=useRef(null),latest=useRef(project),saved=useRef(''),saving=useRef(null),epoch=useRef(0),account=useRef(null),uploading=useRef(false),mediaUrls=useRef(new Map());
 latest.current=project;
 const userId=session?.user?.id;
 const fail=e=>{setStatus('Cloud save failed');onError(e.message || 'Cloud operation failed.');};
 // A shared URL cache survives switching between Paper Edit and Refine.
 const urlFor=(row,options)=>row?.source_detached?''
  :cachedPrivateMediaUrl(mediaUrls.current,row,store.url,options);
 const refresh=async()=>{const token=epoch.current;const [result,trashed,assets]=await Promise.all([store.list(),store.listTrash(),store.listMedia()]);if(token===epoch.current){setRows(result);setTrashRows(trashed.filter(row=>!row.data?.collectionId));setMediaLibrary(assets);}};
 const flush=async()=>{
  if(saving.current) await saving.current;
  const row=activeRef.current;
  if(!row || JSON.stringify(latest.current)===saved.current)return;
  const snapshot=latest.current,token=epoch.current;
  setStatus('Saving to cloud…');
  const task=store.save(row,snapshot).then(next=>{
   if(token!==epoch.current)return;
   activeRef.current=next;setActive(next);saved.current=JSON.stringify(snapshot);
   setStatus(JSON.stringify(latest.current)===saved.current?'Saved to cloud':'Unsaved changes');
   setRows(items=>items.map(item=>item.id===next.id?next:item));
  });
  saving.current=task;
  try{await task;}finally{if(saving.current===task)saving.current=null;}
 };
 const open=async (id,{interview=false,quiet=false}={})=>{
  const token=epoch.current;if(!quiet)setBusy(true);
  try{
   try{await flush();}catch(e){
    if(id!==activeRef.current?.id || !window.confirm('Your changes could not be saved. Discard these local changes and reload the cloud version?'))throw e;
   }
   // Reuse loaded source metadata and signed playback URLs for quick mode changes.
   // Full project opens still fetch current data from the backend.
   const cached=quiet?rows.find(item=>item.id===id):null;
   const row=cached||await loadWorkspaceRow(store,id,interview);
   if(!row.media_ready)throw new Error('This upload did not finish. Delete this incomplete project and upload the video again.');
   // Reuse the already-signed URL when switching editor modes.
   const url=await urlFor(row);
   if(token!==epoch.current)return;
   activeRef.current=row;setActive(row);saved.current=JSON.stringify(row.data);latest.current=row.data;
   localStorage.setItem(`cutscript-last-${userId}`,row.data.collectionId || row.id);
   onOpen(row.data,url);setStatus('Saved to cloud');return true;
  }catch(e){if(token===epoch.current)fail(e);}finally{if(token===epoch.current&&!quiet)setBusy(false);}
 };
 useEffect(()=>{
  epoch.current+=1;const token=epoch.current;mediaUrls.current.clear();
  activeRef.current=null;setActive(null);setRows([]);setTrashRows([]);setMediaLibrary([]);setStatus('');setBusy(false);setRenaming(false);
  if(account.current && account.current!==userId)onClear();
  account.current=userId;
  if(!userId)return;
  setBusy(true);
  (async()=>{
   try{
    const [items,trashed,assets]=await Promise.all([store.list(),store.listTrash(),store.listMedia()]);if(token!==epoch.current)return;setRows(items);setTrashRows(trashed.filter(row=>!row.data?.collectionId));setMediaLibrary(assets);
    const last=localStorage.getItem(`cutscript-last-${userId}`);
    if(last && items.some(row=>row.id===last))await open(last);
    else setBusy(false);
   }catch(e){if(token===epoch.current){fail(e);setBusy(false);}}
  })();
  return()=>{epoch.current+=1;};
 },[userId]);
 useEffect(()=>{
  if(!active || busy || JSON.stringify(project)===saved.current)return;
  setStatus('Unsaved changes');
  const timer=setTimeout(()=>flush().catch(fail),700);
  return()=>clearTimeout(timer);
 },[project,active?.id,busy]);
 useEffect(()=>{
  const handler=e=>{if(busy || (activeRef.current && JSON.stringify(latest.current)!==saved.current)){e.preventDefault();e.returnValue='';}};
  window.addEventListener('beforeunload',handler);return()=>window.removeEventListener('beforeunload',handler);
 },[busy]);
 return {rows,trashRows,mediaLibrary,active,busy,renaming,status,open,flush,
  reuseMedia:async(assetId,projectId=null)=>{
   if(!userId)throw new Error('Sign in to use Media Library.');
   if(busy)throw new Error('Finish the current operation first.');
   setBusy(true);setStatus('Adding media from Library…');
   try{
    await flush();
    const row=await store.reuseMedia(assetId,projectId);
    await refresh();
    const opened=await open(projectId||row.id);
    if(!opened)throw new Error('Media was added. Reopen your project to use it.');
    setStatus('Added from Media Library · no upload needed');
    return row;
   }catch(e){fail(e);throw e;}
   finally{setBusy(false);}
  },
  acceptReview:async feedbackId=>{
   setBusy(true);const token=epoch.current;
   try{await flush();const row=activeRef.current;if(!row)throw new Error('Open the project first.');
    const result=await reviewRpc(supabase,'cutscript_review_owner',{p_project:row.id,p_action:'accept',p_payload:{feedbackId,revision:row.revision}});
    if(token!==epoch.current)return;
    const next=result.project;activeRef.current=next;setActive(next);latest.current=next.data;saved.current=JSON.stringify(next.data);
    setRows(items=>items.map(item=>item.id===next.id?next:item));setStatus('Saved to cloud');return next.data;
   }finally{if(token===epoch.current)setBusy(false);}
  },url:(row,options)=>urlFor(row,options),
  addInterview:async(file,data,onProgress)=>{
   if(!userId || uploading.current)throw new Error('Wait for the current upload to finish.');
   uploading.current=true;setBusy(true);setStatus('Uploading interview…');const token=epoch.current;
   try{
    await flush();
    const row=await store.create(userId,file,data,{onProgress:percent=>{setStatus('Uploading interview · '+percent+'%');onProgress?.(percent);}});
    if(token!==epoch.current)return;
    await refresh();setStatus('Saved to cloud');return row;
   }catch(e){if(token===epoch.current)fail(e);throw e;}
   finally{uploading.current=false;if(token===epoch.current)setBusy(false);}
  },
  // Used for a batch's first interview: create the project and attach it
  // immediately, without racing the old implicit local-upload effect.
  createBatchRoot:async(file,data,onProgress)=>{
   if(!userId || uploading.current)throw new Error('Sign in and finish the current upload first.');
   uploading.current=true;setBusy(true);const token=epoch.current;setStatus('Uploading first interview…');
   try{
    await flush();
    const row=await store.create(userId,file,data,{onProgress});
    const url=await urlFor(row);
    if(token!==epoch.current)throw new Error('Your account changed during this upload.');
    activeRef.current=row;setActive(row);saved.current=JSON.stringify(row.data);
    latest.current=row.data;
    localStorage.setItem(`cutscript-last-${userId}`,row.id);
    onOpen(row.data,url);
    await refresh();setStatus('Saved to cloud');
    return row;
   }catch(error){if(token===epoch.current)fail(error);throw error;}
   finally{uploading.current=false;if(token===epoch.current)setBusy(false);}
  },
  // Save a batch transcription against its latest revision. If it belongs to
  // the active first interview, also update the visible editor and save cursor.
  saveBatchInterview:async(row,data)=>{
   const token=epoch.current;
   const next=await store.save(row,data);
   if(token===epoch.current){
    setRows(items=>items.map(item=>item.id===next.id?next:item));
    if(activeRef.current?.id===next.id){
     activeRef.current=next;setActive(next);saved.current=JSON.stringify(next.data);
     latest.current=next.data;
     onOpen(next.data,await urlFor(next));
    }
   }
   return next;
  },
  saveInterview:async(row,data)=>{
   // Revision checks protect against overwriting edits made in another tab.
   const token=epoch.current;
   const next=await store.save(row,data);
   if(token===epoch.current)setRows(items=>items.map(item=>item.id===next.id?next:item));
   return next;
  },
  rename:async(id,title)=>{
   if(!id || !title.trim() || busy)return false;
   const token=epoch.current;
   setRenaming(true);setBusy(true);
   try{
    await flush();
    if(token!==epoch.current)return false;
    setStatus('Saving project name…');
    const row=await store.get(id);
    if(token!==epoch.current)return false;
    const next=await store.save(row,{...row.data,projectName:title.trim()});
    if(token!==epoch.current)return false;
    if(activeRef.current?.id===id){
     // Keep media, playback, selection, history and any newer local edits intact.
     activeRef.current=next;setActive(next);saved.current=JSON.stringify(next.data);
     latest.current={...latest.current,projectName:next.data.projectName};
     onRename(next.data.projectName);
    }
    setRows(items=>items.map(item=>item.id===id?next:item));
    setStatus(JSON.stringify(latest.current)===saved.current?'Saved to cloud':'Unsaved changes');return true;
   }catch(e){if(token===epoch.current)fail(e);return false;}
   finally{if(token===epoch.current){setRenaming(false);setBusy(false);}}
  },
  detach:()=>{activeRef.current=null;setActive(null);setStatus('');if(userId)localStorage.removeItem(`cutscript-last-${userId}`);},
  upload:async (file,onProgress)=>{
   if(!userId || !file || uploading.current)return;
   uploading.current=true;
   const token=epoch.current;setBusy(true);setStatus('Uploading video…');
   try{
    const snapshot=latest.current;
    const row=await store.create(userId,file,snapshot,{onProgress:percent=>{setStatus('Uploading video · '+percent+'%');onProgress?.(percent);}});
    if(token!==epoch.current)return;
    activeRef.current=row;setActive(row);saved.current=JSON.stringify(snapshot);
    localStorage.setItem(`cutscript-last-${userId}`,row.id);setStatus('Saved to cloud');await refresh();
   }catch(e){if(token===epoch.current)fail(e);}finally{uploading.current=false;if(token===epoch.current)setBusy(false);}
  },
  previewMerge:(source,destination,appendAssembly=true)=>
   store.previewMerge(source,destination,appendAssembly),
  mergeProjects:async(source,destination,appendAssembly=true)=>{
   if(!userId||busy)return false;
   setBusy(true);setStatus('Merging projects…');
   try{
    await flush();
    const result=await store.mergeProjects(source,destination,appendAssembly);
    if(activeRef.current?.id===destination ||
      activeRef.current?.data?.collectionId===destination){
     // The destination revision was changed by the merge transaction. Close
     // the stale editor instead of letting autosave overwrite merged work.
     activeRef.current=null;setActive(null);
     localStorage.removeItem(`cutscript-last-${userId}`);
     onClear();
    }
    await refresh();setStatus('Projects merged · no new media upload');
    return result;
   }catch(e){fail(e);return false;}
   finally{setBusy(false);}
  },
  restoreProject:async row=>{
   if(!userId||busy)return false;
   setBusy(true);setStatus('Restoring project…');
   try{await store.restoreProject(row.id);await refresh();setStatus('Project restored');return true;}
   catch(e){fail(e);return false;}finally{setBusy(false);}
  },
  previewProjectDelete:id=>store.previewProjectDelete(id),
  detachRootInterview:async row=>{
   if(!userId||!row||busy)return false;
   try{await flush();}catch(e){fail(e);return false;}
   const name=row.data?.filename||row.data?.title||'Interview';
   if(!window.confirm(`Remove interview “${name}” from this project? The project, other interviews and Assembly will be kept. The original video stays in Media Library.`))return false;
   setBusy(true);
   try{
    const detached=await store.detachRootInterview(row.id);
    // Also cover removing the first source while a CHILD interview is open.
    // Switch back to the surviving project container before opening Assembly.
    activeRef.current=detached;setActive(detached);
    saved.current=JSON.stringify(detached.data);latest.current=detached.data;
    onOpen(detached.data,'');setStatus('Interview removed · media kept in Library');
    await refresh();return true;
   }catch(e){fail(e);return false;}
   finally{setBusy(false);}
  },
  remove:async(row,{deleteMedia=false,confirmed=false,mode='trash'}={})=>{
   if(!userId||busy)return false;
   const permanent=mode==='permanent';
   let current;
   try{
    await flush();
    current=permanent?await store.get(row.id):(await store.list()).find(item=>item.id===row.id);
   }catch(e){fail(e);return false;}
   if(!current){onError('This project or interview no longer exists. Refresh and try again.');return false;}
   const child=Boolean(current.data?.collectionId);
   if(child){
    const fresh=await store.list();
    const blocker=interviewDeletionBlocker(fresh,current);
    if(blocker){onError(blocker);return false;}
    if(!window.confirm(`Remove interview “${current.data.filename||'Interview'}” from this project? Media stays in Library.`))return false;
   }else{
    if(!confirmed){onError('Confirm project deletion in the project dialog.');return false;}
    if(permanent&& !current.deleted_at){onError('Move a project to Trash before deleting it permanently.');return false;}
    if(!permanent&&current.deleted_at){onError('This project is already in Trash.');return false;}
   }
   setBusy(true);
   try{
    await flush();
    const outcome=child
      ?(await store.remove(current),{cleanup_pending:false})
      :permanent
       ?await store.removeProject(current.id,Boolean(deleteMedia))
       :await store.trashProject(current.id);
    const activeRow=activeRef.current;
    if(activeRow&&(activeRow.id===current.id||
      (!child&&activeRow.data?.collectionId===current.id))){
     activeRef.current=null;setActive(null);
     localStorage.removeItem(`cutscript-last-${userId}`);
     onClear();setStatus('');
    }
    await refresh();
    if(outcome.cleanup_pending)onError(
     'Project permanently deleted. Some videos are pending media cleanup and will be retried on refresh.'
    );
    else if(!child)setStatus(permanent?'Project permanently deleted':'Moved to Trash · recoverable');
    return true;
   }catch(e){fail(e);return false;}finally{setBusy(false);}
  },
  file:(row=activeRef.current)=>store.file(row),
  retry:()=>flush().catch(fail)
 };
}
